import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { ROUTES } from 'shared/core';
import { AppShellComponent, DeniedComponent, KfSessionGuard, LoginComponent, LogoutComponent, TenantSelectComponent } from 'shared';
import { ACCESSORIES_MOUNT_ROUTES, TANNERY_MOUNT_ROUTES, VERTICAL_PRINT_ROUTES } from './vertical-mounts';

// Components that remain eagerly loaded (not in any feature module)
import { SupplierListComponent } from './SupplierManagement/Supplier/supplier-list/supplier-list.component';
import { CustomerListComponent } from './CustomerManagement/customer-list/customer-list.component';

// Print components (outside home children, remain in root module)
import { MixingprintComponent } from './Production/mixingprint/mixingprint.component';
import { MixingTicketPrintComponent } from './Production/mixingticketprint/mixing-ticket-print.component';
import { MasterMixTicketPrintComponent } from './Production/mixingticketprint/master-mix-ticket-print/master-mix-ticket-print.component';
import { MixingChildTicketPrintComponent } from './Production/mixingticketprint/mixing-child-ticket-print/mixing-child-ticket-print.component';
import { ProcessprintComponent } from './Production/processprint/processprint.component';
import { OutPassPrintComponent } from './OutPass/outpassPrint/outpassprint.component';
import { OutPassPackingListPrintComponent } from './OutPass/outpassPackingListPrint/outpasspackinglistprint.component';
import { InspectionPrintComponent } from './Production/WorkPlan/inspectionprint/inspectionprint.component';
import { JumboPrintComponent } from './Production/WorkPlan/jumboprint/jumboprint.component';
import { InspectionAllPrintComponent } from './Production/WorkPlan/inspectionallprint/inspectionallprint.component';
import { PackingListPrintComponent } from 'shared/ui';
import { CostingListPrintComponent } from './Costing/CostingListPrint/CostingListPrint.component';
import { InspectionDetailsPrintComponent } from './Production/WorkPlan/InspectionDetailsPrint/InspectionDetailsPrint.component';
import { InspectionDetailsMixingPrintComponent } from './Production/WorkPlan/InspectionDetailsMixingPrint/InspectionDetailsMixingPrint.component';
import { PackagingListEmailComponent } from './Dispatch/Packaging/Email/PackagingListEmail.component';
import { SingleOrderCostingPrint } from './Costing/SingleOrderCostingPrint/SingleOrderCostingPrint.component';
import { FormulationPrintComponent } from './Production/FormulationCode/Print/formulationprint.component';
import { VesselTagPrintComponent } from './Master/vessel/vessel-tag-print.component';
import { GatepassprintComponent } from './GateManagement/GatePassPrint/gatepassprint.component';
import { proformaPrintComponent } from './SalesOrder/proformaInvoice/Print/proformaPrint.component';
import { CostEstimationListPrintComponent } from './Costing/Estimation/PrintList/costEstimationListPrint.component';
import { CostEstimationPrintComponent } from './Costing/Estimation/Print/costEstimationPrint.component';

// Components that remain in home children (not in any feature module)
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { PaymentBlockedComponent } from './pages/payment-blocked/payment-blocked.component';
import { ProductionDowntimeReasonComponent } from './ProductionMasters/production-downtime-reason/production-downtime-reason.component';
import { LowGradeReasonComponent } from './ProductionMasters/low-grade-reason/low-grade-reason.component';
import { ProductTransferComponent } from './Product/producttransfer/producttransfer.component';
import { NotificationComponent } from './Notification/ProductionStagesList/notification/notification.component';
import { NotificationTypeConfigurationComponent } from './Notification/NotificationTypeConfiguration/notification-type-configuration.component';
import { NotificationGroupComponent } from './Notification/NotificationGroup/notification-group/notification-group.component';
import { WhatsAppTemplateListComponent } from './Notification/whats-app-template-list/whats-app-template-list.component';
import { WhatsappConfigComponent } from './Notification/whatsapp-config/whatsapp-config.component';
import { EmailGroupMappingComponent } from './Notification/email-group-mapping/email-group-mapping.component';
import { DashboardHubComponent } from './Dashboard/DashboardHub/DashboardHub.component';
import { IATFDocumentManagementComponent } from './Features/IATFDocumentManagement/IATFDocumentManagement.component';

const routes: Routes = [
  // { path: '', pathMatch: 'full', redirectTo: '/start' },
  // { path: '', pathMatch: 'full', redirectTo: '/welcome' }, // DEPRECATED - now redirects to /landing
  { path: '', pathMatch: 'full', redirectTo: '/welcome' },
  {
    path: 'home',
    // The shared shell, not a rexine one: the same layout, sidebar and header that every vertical
    // renders inside. An NgModule application can route to a standalone component directly.
    component: AppShellComponent,
    children: [
      // A bare /home is the welcome page, for every unit type; nothing else matches an empty path.
      { path: '', pathMatch: 'full', redirectTo: 'welcome' },
      // Lazy-loaded feature modules (empty path so child routes are flat under 'home')
      { path: '', loadChildren: () => import('./Master/master.module').then(m => m.MasterModule) },
      { path: '', loadChildren: () => import('./Attributes/attributes.module').then(m => m.AttributesModule) },
      { path: '', loadChildren: () => import('./GateManagement/gate-management.module').then(m => m.GateManagementModule) },
      { path: '', loadChildren: () => import('./Admin/admin.module').then(m => m.AdminModule) },
      { path: '', loadChildren: () => import('./Production/production.module').then(m => m.ProductionModule) },
      { path: '', loadChildren: () => import('./Production/ReleasePaper/release-paper.module').then(m => m.ReleasePaperModule) },
      { path: '', loadChildren: () => import('./Costing/costing.module').then(m => m.CostingModule) },
      { path: '', loadChildren: () => import('./Consumption/consumption.module').then(m => m.ConsumptionModule) },
      { path: '', loadChildren: () => import('./SalesOrder/sales-order.module').then(m => m.SalesOrderModule) },
      // Purchase Order (and Demand, whose one route it carries) is a common module
      // and lives in the library; every vertical mounts this same one.
      { path: '', loadChildren: () => import('shared/features/purchase-order').then(m => m.PurchaseOrderModule) },
      { path: '', loadChildren: () => import('./Dispatch/dispatch.module').then(m => m.DispatchModule) },
      // Issue is a common module and lives in the library; every vertical mounts this same one.
      { path: '', loadChildren: () => import('shared/features/issue').then(m => m.IssueModule) },
      // AI (Usage/Settings) is a common module and lives in the library; every vertical mounts this same one.
      { path: '', loadChildren: () => import('shared/features/ai').then(m => m.AiModule) },
      // Inventory is a common module and lives in the library; every vertical mounts this same one.
      { path: '', loadChildren: () => import('shared/features/inventory').then(m => m.InventoryModule) },
      { path: '', loadChildren: () => import('./GRNManagement/grn-management.module').then(m => m.GrnManagementModule) },
      { path: '', loadChildren: () => import('./Features/TallyIntegration/tally-integration.module').then(m => m.TallyIntegrationModule) },
      { path: '', loadChildren: () => import('./Features/Integrations/integrations.module').then(m => m.IntegrationsModule) },

      // Return Stock Routes - Lazy Loaded Feature Module
      {
        path: 'return-stock',
        loadChildren: () => import('./Features/ReturnStockManagement/return-stock.module')
          .then(m => m.ReturnStockModule),
        canActivate: [KfSessionGuard],
        data: {
          title: 'Return Stock Management',
          breadcrumb: 'Return Stock'
        }
      },

      ...TANNERY_MOUNT_ROUTES,
      ...ACCESSORIES_MOUNT_ROUTES,
      // The denied page carries KfSessionGuard like every other component route under home.
      // Without it a signed-out deep link to /home/tannery/... lands here rather than at
      // sign-in: canMatch runs first, there is no tenant so getUnitType() is null, the gate
      // fails, the fallback above fires, and the mount's own canActivate never runs. A
      // signed-in rexine tenant still reaches the page, which is who it is written for.
      {
        path: 'denied/:area',
        component: DeniedComponent,
        canActivate: [KfSessionGuard],
        title: 'Not available here | KanzenFlow by Misay Innovations'
      },
      {
        path: 'denied',
        component: DeniedComponent,
        canActivate: [KfSessionGuard],
        title: 'Not available here | KanzenFlow by Misay Innovations'
      },

      // Routes that remain eagerly loaded (not in any feature module)
      {
        path: 'supplierlist',
        component: SupplierListComponent,
        canActivate: [KfSessionGuard],
        title: 'Supplier | Supplier List | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'customerlist',
        component: CustomerListComponent,
        canActivate: [KfSessionGuard],
        title: 'Customer | Customer List | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'productionmaster/downtimereason',
        component: ProductionDowntimeReasonComponent,
        canActivate: [KfSessionGuard],
        title: 'Master | Production | Downtime Reason | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'productionmaster/lowgradereason',
        component: LowGradeReasonComponent,
        canActivate: [KfSessionGuard],
        title: 'Master | Production | Low Grade Reason | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'product/producttransfer',
        component: ProductTransferComponent,
        canActivate: [KfSessionGuard],
        title: 'Product | Product Transfer | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'compliance/iatf-document-management',
        component: IATFDocumentManagementComponent,
        canActivate: [KfSessionGuard],
        title: 'Master | Compliance | IATF Document Management | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'notificationlist',
        component: NotificationComponent,
        canActivate: [KfSessionGuard],
        title: 'Notification | Production Stages List | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'notificationTypeConfiguration',
        component: NotificationTypeConfigurationComponent,
        canActivate: [KfSessionGuard],
        title: 'Notification | Notification Type Configuration | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'notificationGrouplist',
        component: NotificationGroupComponent,
        canActivate: [KfSessionGuard],
        title: 'Notification | Notification Group List | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'whatsAppTemplateListComponent',
        component: WhatsAppTemplateListComponent,
        canActivate: [KfSessionGuard],
        title: 'Notification | WhatsApp Template List | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'WhatsappConfigList',
        component: WhatsappConfigComponent,
        canActivate: [KfSessionGuard],
        title: 'Notification | Whatsapp Config List | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'EmailGroupMappingList',
        component: EmailGroupMappingComponent,
        canActivate: [KfSessionGuard],
        title: 'Notification | Email Group Mapping List | Zaibunco | KanzenFlow by Misay Innovations'
      },
      {
        path: 'unauthorized',
        component: UnauthorizedComponent,
        canActivate: [KfSessionGuard],
        title: 'Unauthorized | Zaibunco | KanzenFlow by Misay Innovations'
      },

      // Direct lazy-loaded feature-wise report modules with meaningful chunk names
      // Production Reports
      {
        path: 'reports/production',
        loadChildren: () =>
          import(/* webpackChunkName: "production-reports" */ './Features/Reports/production-reports/production-reports.module').then((m) => m.ProductionReportsModule)
      },
      // Stock Reports
      {
        path: 'reports/stock',
        loadChildren: () =>
          import(/* webpackChunkName: "stock-reports" */ './Features/Reports/stock-reports/stock-reports.module').then((m) => m.StockReportsModule)
      },

      // Purchase Reports
      {
        path: 'reports/purchase',
        loadChildren: () =>
          import(/* webpackChunkName: "purchase-reports" */ './Features/Reports/purchase-reports/purchase-reports.module').then((m) => m.PurchaseReportsModule)
      },

      // Sales Reports
      {
        path: 'reports/sales',
        loadChildren: () =>
          import(/* webpackChunkName: "sales-reports" */ './Features/Reports/sales-reports/sales-reports.module').then((m) => m.SalesReportsModule)
      },

      // Analytics Reports
      {
        path: 'reports/analytics',
        loadChildren: () =>
          import(/* webpackChunkName: "analytics-reports" */ './Features/Reports/analytics-reports/analytics-reports.module').then((m) => m.AnalyticsReportsModule)
      },

      {
        path: 'dashboard',
        component: DashboardHubComponent,
        canActivate: [KfSessionGuard],
        title: 'Dashboards | Zaibunco | KanzenFlow by Misay Innovations',
        pathMatch: 'full'
      },
      {
        path: 'dashboard/reporting',
        canActivate: [KfSessionGuard],
        title: 'Dashboard | Reporting Dashboard | Zaibunco | KanzenFlow by Misay Innovations',
        loadChildren: () =>
          import(/* webpackChunkName: "reporting-dashboard" */ './Dashboard/GateDashboard/reporting-dashboard.module').then((m) => m.ReportingDashboardModule)
      },
      {
        path: 'dashboard/production',
        canActivate: [KfSessionGuard],
        title: 'Dashboard | Production | Zaibunco | KanzenFlow by Misay Innovations',
        loadChildren: () =>
          import(/* webpackChunkName: "production-dashboard" */ './Dashboard/ProductionDashboard/production-dashboard.module').then((m) => m.ProductionDashboardModule)
      },
      {
        path: 'dashboard/inventory',
        canActivate: [KfSessionGuard],
        title: 'Dashboard | Inventory | Zaibunco | KanzenFlow by Misay Innovations',
        loadChildren: () =>
          import(/* webpackChunkName: "inventory-dashboard" */ './Dashboard/InventoryDashboard/inventory-dashboard.module').then((m) => m.InventoryDashboardModule)
      },
      {
        path: 'dashboard/performance',
        canActivate: [KfSessionGuard],
        title: 'Dashboard | Production Performance | Zaibunco | KanzenFlow by Misay Innovations',
        loadChildren: () =>
          import(/* webpackChunkName: "performance-dashboard" */ './Dashboard/ProductionPerformanceDashboard/performance-dashboard.module').then((m) => m.PerformanceDashboardModule)
      },
      {
        path: 'dashboard/dispatch',
        canActivate: [KfSessionGuard],
        title: 'Dashboard | Dispatch | Zaibunco | KanzenFlow by Misay Innovations',
        loadChildren: () =>
          import(/* webpackChunkName: "dispatch-dashboard" */ './Dashboard/DispatchDashboard/dispatch-dashboard.module').then((m) => m.DispatchDashboardModule)
      },
      {
        path: 'dashboard/sales',
        canActivate: [KfSessionGuard],
        title: 'Dashboard | Sales | Zaibunco | KanzenFlow by Misay Innovations',
        loadChildren: () =>
          import(/* webpackChunkName: "sales-dashboard" */ './Dashboard/SalesDashboard/sales-dashboard.module').then((m) => m.SalesDashboardModule)
      },
      {
        path: 'dashboard/store-capacity-management',
        canActivate: [KfSessionGuard],
        title: 'Dashboard | Store Capacity Management | Zaibunco | KanzenFlow by Misay Innovations',
        loadChildren: () =>
          import(/* webpackChunkName: "store-capacity-dashboard" */ './Dashboard/StoreCapacityManagement/store-capacity-dashboard.module').then((m) => m.StoreCapacityDashboardModule)
      },
      {
        path: 'welcome', // ROUTES.DASHBOARD.WELCOME.replace('/home/', '')
        loadChildren: () =>
          import('./pages/welcome/welcome.module').then((m) => m.WelcomeModule),
        title: 'Welcome | Zaibunco | KanzenFlow by Misay Innovations'
      },
    ],
  },
  // Sign-in, tenant selection and sign-out, all from the shared library. Both
  // /welcome and /login render the same page: they were two routes with one job
  // between them, and every bookmark, every link and the registered
  // postLogoutRedirectUri still point at one or the other.
  {
    path: 'welcome',
    component: LoginComponent,
    title: 'Welcome | KanzenFlow by Misay Innovations'
  },
  {
    path: 'login',
    component: LoginComponent,
    title: 'Login | KanzenFlow by Misay Innovations'
  },
  {
    path: 'tenant-select',
    component: TenantSelectComponent,
    title: 'Select Company | KanzenFlow by Misay Innovations'
  },
  {
    path: 'logout',
    component: LogoutComponent,
    title: 'Logout | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'outpass/print/:id',
    component: OutPassPrintComponent,
    canActivate: [KfSessionGuard],
    title: 'Out Pass | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'outpass/packinglistprint/:id',
    component: OutPassPackingListPrintComponent,
    canActivate: [KfSessionGuard],
    title: 'Out Pass | Packing List Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'jumboprint/:id/:JumboId',
    component: JumboPrintComponent,
    title: 'Production | Jumbo | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'inspectionprint/:id/:JumboId/:InspectionId',
    component: InspectionPrintComponent,
    title: 'Production | Inspection | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'inspectionprint/:id/:JumboId',
    component: InspectionPrintComponent,
    title: 'Production | Inspection | Print All | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'inspectionallprint/:id/:JumboId',
    component: InspectionAllPrintComponent,
    title: 'Production | Inspection | All Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'production/formulationprint/:id',
    component: FormulationPrintComponent,
    title: 'Production | Formulation | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'production/mixingprint/:id',
    component: MixingprintComponent,
    title: 'Production | Mixing | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'print/mixing-ticket/:id',
    component: MixingTicketPrintComponent,
    title: 'Mixing Ticket | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'print/master-mix-ticket/:masterMixBatchNumber',
    component: MasterMixTicketPrintComponent,
    title: 'Master Mix Ticket | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'print/mixing-child-ticket/:masterMixBatchNumber/:mixingChildTicketNumber',
    component: MixingChildTicketPrintComponent,
    title: 'Mixing Child Ticket | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'inspectiondetailsprint/:id',
    component: InspectionDetailsPrintComponent,
    title: 'Production | Inspection | Details | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'inspectiondetailsmixingprint/:id/:mixingName',
    component: InspectionDetailsMixingPrintComponent,
    title: 'Production | Inspection | Details | Mixing Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'production/processprint/:id',
    component: ProcessprintComponent,
    title: 'Production | Process | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },

  {
    path: 'vesseltagprint/:id',
    component: VesselTagPrintComponent,
    title: 'Vessel ID Tag | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    // loadComponent: the card is a standalone component of the lazy Release Paper folder, so the eagerly
    // evaluated routing file does not pull it into the initial graph.
    path: 'releasepaperinspectionprint/:inspectionId',
    canActivate: [KfSessionGuard],
    loadComponent: () => import('./Production/ReleasePaper/InspectionPrint/inspection-print.component').then(m => m.InspectionPrintComponent),
    title: 'Release Paper | Inspection Card | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'gatepassprint/:id',
    component: GatepassprintComponent,
    title: 'Gate Pass | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    // loadComponent, not `component:`. The slip and the barcode label sheet below
    // are standalone now so tannery can route to them too, and naming a class
    // from shared/ui in an eagerly-evaluated file is the M2d mistake: a secondary
    // entry point is one FESM bundle.
    path: 'rejectedstockslip/:id',
    loadComponent: () => import('shared/ui').then(m => m.RejectedStockSlipComponent),
    title: 'Rejected Stock Slip | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    // loadComponent, not `component:`. Naming the class here would be a static
    // import of shared/features/purchase-order from an eagerly-evaluated file,
    // and a secondary entry point is one FESM bundle — the PO add, list, demand
    // list and invoice screens would land in the initial graph with it.
    path: 'poprint/:id',
    loadComponent: () => import('shared/features/purchase-order').then(m => m.POprintComponent),
    title: 'Purchase Order | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'poprint/:id/:type',
    loadComponent: () => import('shared/features/purchase-order').then(m => m.POprintComponent),
    title: 'Purchase Order | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'poemail/:id',
    loadComponent: () => import('shared/features/purchase-order').then(m => m.POEmailComponent),
    title: 'Purchase Order | Email | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'dispatchpackagingemail/:id',
    component: PackagingListEmailComponent,
    title: 'Dispatch | Packaging | Email | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'proformaPrint/:id',
    component: proformaPrintComponent,
    canActivate: [KfSessionGuard],
    title: 'Sales Order | Proforma | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'packinglistprint/:id',
    component: PackingListPrintComponent,
    canActivate: [KfSessionGuard],
    title: 'Dispatch | Packing List | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'costinglistprint',
    component: CostingListPrintComponent,
    canActivate: [KfSessionGuard],
    title: 'Costing | Costing List | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'costestimationlistprint',
    component: CostEstimationListPrintComponent,
    canActivate: [KfSessionGuard],
    title: 'Costing | Cost Estimation List | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'costestimationprint/:id',
    component: CostEstimationPrintComponent,
    canActivate: [KfSessionGuard],
    title: 'Costing | Cost Estimation | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'ordercostingprint/:id/:type',
    component: SingleOrderCostingPrint,
    canActivate: [KfSessionGuard],
    title: 'Costing | Order Costing | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
    title: 'Unauthorized | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'payment-blocked',
    component: PaymentBlockedComponent,
    title: 'Access Denied | Payment Required'
  },
  {
    // loadComponent, not `component:`. Naming the class here would be a static
    // import of shared/features/issue from an eagerly-evaluated file, and a
    // secondary entry point is one FESM bundle — the Issue add and list screens
    // would land in the initial graph with it.
    path: 'issueslipprint/:id',
    loadComponent: () => import('shared/features/issue').then(m => m.IssueSlipPrintComponent),
    title: 'Issue | Issue Slip | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'barcodelabel/:StockProductId/:StockId/:ProductId/:NumberOfLabels/:action',
    loadComponent: () => import('shared/ui').then(m => m.BarcodeLabelPrintComponent),
    title: 'Barcode | Barcode Label | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  {
    path: 'barcodelabel/:action',
    loadComponent: () => import('shared/ui').then(m => m.BarcodeLabelPrintComponent),
    title: 'Barcode | Barcode Label | Print | Zaibunco | KanzenFlow by Misay Innovations'
  },
  ...VERTICAL_PRINT_ROUTES,

];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    initialNavigation: 'enabledNonBlocking',
  })],
  exports: [RouterModule],
})
export class PmsUIAppRoutingModule { }
